/**
 * ReplayPlayer — top-level replay view. Loads a match's NDJSON via
 * useReplayPlayback, renders timeline + controls + a human-readable
 * event log that updates as the cursor advances.
 *
 * Visual board reconstruction is deferred — V1 shows the event log + a
 * "current event" highlight. V2 will reuse the wonders-table renderer
 * via a shared event-reducer (planned: import games-wonders applyEvent
 * client-side).
 */

import type { ReactElement } from 'react';
import { EventMarkers } from './EventMarkers.js';
import { ReplayControls } from './ReplayControls.js';
import { useReplayPlayback } from './useReplayPlayback.js';

type Props = {
  matchUlid: string;
};

export function ReplayPlayer({ matchUlid }: Props): ReactElement {
  const hook = useReplayPlayback(matchUlid);

  if (hook.status === 'loading') {
    return (
      <Centered>
        <div className="text-cosmos-400">Loading replay…</div>
      </Centered>
    );
  }
  if (hook.status === 'error') {
    return (
      <Centered>
        <div className="text-rose-400">Replay error: {hook.error}</div>
      </Centered>
    );
  }

  const meta = hook.meta;
  const current = hook.events[hook.cursor];
  const startAt = hook.events[0]?.at ?? 0;

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-5xl flex-col gap-4 bg-cosmos-950 px-4 py-6 text-cosmos-100">
      <header className="border-cosmos-800 border-b pb-3">
        <h1 className="text-xl font-semibold">Replay — {matchUlid}</h1>
        {meta ? (
          <p className="text-xs text-cosmos-400">
            {meta.players.join(' vs ')} · winner: <span className="font-mono">{meta.winner}</span> ·
            ended {new Date(meta.endedAt).toLocaleString()}
          </p>
        ) : null}
      </header>

      <ReplayControls hook={hook} />

      <section>
        <h2 className="mb-1 text-xs font-medium uppercase tracking-wide text-cosmos-400">
          Timeline
        </h2>
        <EventMarkers hook={hook} />
      </section>

      <section className="flex-1 overflow-hidden rounded-md border border-cosmos-700 bg-cosmos-900/60">
        <h2 className="border-cosmos-800 border-b bg-cosmos-900/80 px-3 py-2 text-xs font-medium uppercase tracking-wide text-cosmos-400">
          Event log
        </h2>
        <ol className="max-h-[60vh] overflow-y-auto p-3 font-mono text-xs text-cosmos-200">
          {hook.played.map((ev, i) => {
            const offset = ev.at - startAt;
            const isCurrent = i === hook.cursor;
            return (
              <li
                key={`${ev.seq ?? i}-${ev.at}`}
                className={`flex gap-3 border-b border-cosmos-800/50 py-1 ${
                  isCurrent ? 'bg-amber-900/30 font-semibold text-amber-200' : ''
                }`}
              >
                <span className="w-12 text-cosmos-500">+{(offset / 1000).toFixed(1)}s</span>
                <span className="w-32 text-cosmos-500 truncate">{ev.actor ?? '—'}</span>
                <span className="w-32 text-cosmos-300">{ev.kind}</span>
                <span className="flex-1 text-cosmos-300 truncate">{describeEvent(ev)}</span>
              </li>
            );
          })}
        </ol>
      </section>

      {current ? (
        <footer className="rounded-md border border-amber-700/40 bg-amber-900/20 p-3 text-xs">
          <div className="font-mono uppercase tracking-wide text-amber-300">Current event</div>
          <pre className="mt-1 max-h-40 overflow-y-auto whitespace-pre-wrap text-cosmos-200">
            {JSON.stringify(current, null, 2)}
          </pre>
        </footer>
      ) : null}
    </div>
  );
}

function describeEvent(ev: { kind: string; payload: Record<string, unknown> }): string {
  if (ev.kind === 'TableActionApplied') {
    const action = ev.payload?.action as
      | {
          kind?: string;
          cardId?: string;
          from?: { kind?: string };
          to?: { kind?: string; realm?: string };
        }
      | undefined;
    if (!action) return '';
    if (action.kind === 'MoveCard') {
      return `${action.cardId ?? '?'} ${action.from?.kind ?? '?'} → ${action.to?.realm ?? action.to?.kind ?? '?'}`;
    }
    return action.kind ?? '';
  }
  return '';
}

function Centered({ children }: { children: ReactElement }): ReactElement {
  return (
    <div className="flex min-h-[60vh] items-center justify-center bg-cosmos-950 text-cosmos-100">
      {children}
    </div>
  );
}
